import { getTiandituGeocoderBaseUrl } from '@/utils/tiandituConfig'
import {
  getGlobalCoordAddressCache,
  isValidResolvedAddress,
  normalizeCoordKey
} from '@/utils/coordAddressResolver'

/**
 * 导出场景自适应并发数（浏览器侧网络并发，非 OS 多进程）。
 * 数量越大开越多 worker，上限避免触发天地图限流。
 */
export function pickExportRegeoConcurrency(total) {
  const n = Number(total) || 0
  if (n <= 0) return 1
  if (n <= 10) return Math.min(4, n)
  if (n <= 50) return 8
  if (n <= 200) return 12
  if (n <= 1000) return 16
  if (n <= 5000) return 20
  return 24
}

/**
 * 解析天地图逆地理 JSON，优先 formatted_address
 */
export function parseTiandituRegeoResponse(data) {
  if (data && String(data.status) === '0' && data.result) {
    const r = data.result
    const ac = r.addressComponent || {}
    const addr =
      (typeof r.formatted_address === 'string' && r.formatted_address) ||
      (typeof ac.address === 'string' && ac.address) ||
      (typeof ac.city === 'string' && ac.city) ||
      ''
    return { success: true, address: addr || '未知位置' }
  }
  return {
    success: false,
    address: '未知位置',
    msg: data && data.msg != null ? String(data.msg) : ''
  }
}

/**
 * 单点逆地理（坐标系与原先高德逆地理一致：传入 GCJ-02）
 * @returns {Promise<{ success: boolean, des: string, lon: number, lat: number, pro: string, cit: string, cou: string, errorMsg: string }>}
 */
export async function tiandituReverseGeocode(lon, lat) {
  const lng = Number(lon)
  const La = Number(lat)
  if (!Number.isFinite(lng) || !Number.isFinite(La)) {
    return {
      success: false,
      des: '未知位置',
      lon: lng,
      lat: La,
      pro: '',
      cit: '',
      cou: '',
      errorMsg: 'invalid_coord'
    }
  }

  const base = getTiandituGeocoderBaseUrl()
  const qs = new URLSearchParams()
  qs.set('lon', String(lng))
  qs.set('lat', String(La))
  const url = `${base}?${qs.toString()}`

  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' })
    if (!res.ok) {
      throw new Error(`http_${res.status}`)
    }
    const data = await res.json()
    const parsed = parseTiandituRegeoResponse(data)
    return {
      success: parsed.success,
      des: parsed.address,
      lon: lng,
      lat: La,
      pro: '',
      cit: '',
      cou: '',
      errorMsg: parsed.success ? '' : (parsed.msg || 'fail')
    }
  } catch (e) {
    console.warn('天地图逆地理失败', e)
    return {
      success: false,
      des: '未知位置',
      lon: lng,
      lat: La,
      pro: '',
      cit: '',
      cou: '',
      errorMsg: e && e.message ? String(e.message) : 'network_error'
    }
  }
}

function toResultItem(r) {
  return {
    lon: r.lon,
    lat: r.lat,
    pro: r.pro || '',
    cit: r.cit || '',
    cou: r.cou || '',
    des: r.des,
    success: r.success,
    errorMsg: r.errorMsg || ''
  }
}

function resultFromCachedAddress(lon, lat, address) {
  const ok = isValidResolvedAddress(address)
  return toResultItem({
    lon,
    lat,
    des: ok ? String(address).trim() : '未知位置',
    success: ok,
    errorMsg: ok ? '' : 'cache_miss'
  })
}

/**
 * 按「coord 字符串 lon,lat」顺序逐条请求，兼容原 decodeAddressList 的 resultList 结构
 */
export async function tiandituDecodeCoordsSequential(coords, onEachDone) {
  const resultList = []
  for (let i = 0; i < coords.length; i++) {
    const coord = coords[i]
    const parts = String(coord).split(',')
    const lon = Number(parts[0])
    const lat = Number(parts[1])
    const r = await tiandituReverseGeocode(lon, lat)
    resultList.push(toResultItem(r))
    if (typeof onEachDone === 'function') {
      await onEachDone(i + 1, coords.length)
    }
  }
  return { resultList }
}

/**
 * 多并发天地图逆地理（保序），优先读全局坐标缓存。
 * @param {string[]} coords 如 ["116.3,39.9", ...]
 * @param {object} [options]
 * @param {number} [options.concurrency] 并发数，默认按数量自适应
 * @param {() => boolean} [options.shouldCancel] 返回 true 时中止
 * @param {(done: number, total: number) => (void|Promise<void>)} [options.onProgress]
 * @param {boolean} [options.useCache=true]
 * @param {boolean} [options.writeCache=true]
 * @returns {Promise<{ resultList: Array }>}
 */
export async function tiandituDecodeCoordsConcurrent(coords, options = {}) {
  const list = Array.isArray(coords) ? coords : []
  const total = list.length
  const resultList = new Array(total)
  if (!total) return { resultList: [] }

  const {
    shouldCancel,
    onProgress,
    fallbackResolve,
    useCache = true,
    writeCache = true
  } = options
  const concurrency = Math.max(
    1,
    Math.min(
      total,
      Number(options.concurrency) > 0
        ? Math.floor(Number(options.concurrency))
        : pickExportRegeoConcurrency(total)
    )
  )

  const globalCache = useCache || writeCache ? getGlobalCoordAddressCache() : null
  let cursor = 0
  let done = 0

  const runOne = async () => {
    while (cursor < total) {
      if (typeof shouldCancel === 'function' && shouldCancel()) {
        throw new Error('__EXPORT_CANCELLED__')
      }
      const idx = cursor
      cursor += 1
      if (idx >= total) return

      const coord = list[idx]
      const parts = String(coord).split(',')
      const lon = Number(parts[0])
      const lat = Number(parts[1])
      const key = normalizeCoordKey(lon, lat)

      let item = null
      if (useCache && globalCache && key) {
        const cached = globalCache.get(key)
        if (isValidResolvedAddress(cached)) {
          item = resultFromCachedAddress(lon, lat, cached)
        }
      }
      if (!item) {
        const r = await tiandituReverseGeocode(lon, lat)
        item = toResultItem(r)
        if (!item.success && typeof fallbackResolve === 'function') {
          const fallbackAddress = await fallbackResolve(lon, lat)
          item = resultFromCachedAddress(lon, lat, fallbackAddress)
        }
        if (writeCache && globalCache && key && item.success && isValidResolvedAddress(item.des)) {
          globalCache.set(key, String(item.des).trim())
        }
      }

      resultList[idx] = item
      done += 1
      if (typeof onProgress === 'function') {
        await onProgress(done, total)
      }
    }
  }

  const workers = Array.from({ length: concurrency }, () => runOne())
  await Promise.all(workers)
  return { resultList }
}
